package one.healthium.life

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.receiveAsFlow
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.paywall.PaywallInteractor
import one.healthium.domain.paywall.models.PaywallSet
import one.healthium.domain.user.UserInteractor
import one.healthium.domain.user.UserModel
import one.healthium.log_food.data.FoodPreloadContent
import one.healthium.onboarding.domain.OnboardingInteractor
import one.healthium.onboarding.domain.OnboardingModel
import one.healthium.uscreen.domain.UScreenInteractor
import one.healthium.uscreen.domain.models.UScreenFlowModel
import one.healthium.ui.common.vm.BaseViewModel
import one.primehealth.analytics.AppAnalyticsPlatform
import one.primehealth.app.AppRestartController

class AppLaunchViewModel(
    private val userInteractor: UserInteractor,
    private val onboardingInteractor: OnboardingInteractor,
    private val uscreenInteractor: UScreenInteractor,
    private val paywallInteractor: PaywallInteractor,
    private val foodPreloadContent: FoodPreloadContent,
    private val appRestartController: AppRestartController,
    private val appAnalyticsPlatform: AppAnalyticsPlatform,
    private val appPreferences: AppPreferences,
) : BaseViewModel() {

    private val _state = MutableSharedFlow<AppLaunchState>(replay = 1)
    val state = _state.asSharedFlow()

    init {
        launch {
            try {
                foodPreloadContent.preload()
                _state.emit(fetchUser())
            } catch (e: Exception) {
                Logger.e("AppLaunchViewModel init error", e)
            }
        }

        launch {
            appRestartController.channel
                .receiveAsFlow()
                .collect {
                    Logger.d("AppLaunch restart!")
                    _state.emit(fetchUser())
                }
        }
    }

    private suspend fun fetchUser(): AppLaunchState {
        val user = userInteractor.getUser()
        return processUser(user)
    }

    private suspend fun processUser(user: UserModel): AppLaunchState {
        appAnalyticsPlatform.updateUser(user)

        var onboarding: OnboardingModel? = null
        var uscreen: UScreenFlowModel? = null

        if (user.isLocalId && appPreferences.onboardingPassed.not()) {
            uscreen = try {
                uscreenInteractor.get()
            } catch (e: Exception) {
                Logger.w("UScreen flow not available, falling back to classic onboarding", e)
                null
            }
            if (uscreen == null) {
                onboarding = onboardingInteractor.get()
            }
        }
        val paywallSet = paywallInteractor.getPaywallSet()
        return AppLaunchState(
            uscreen = uscreen,
            onboarding = onboarding,
            paywallSet = paywallSet,
            user = user,
        )
    }
}

data class AppLaunchState(
    val onboarding: OnboardingModel?,
    val uscreen: UScreenFlowModel?,
    val paywallSet: PaywallSet?,
    val user: UserModel,
)